import { useState, useEffect, useContext } from "react";
import { useSelector } from "react-redux";
import { AuthContext } from "../../context/AuthContext";
import { axiosInstance } from "../../api/axios";
import { isMobile } from 'react-device-detect'; // 모바일 디바이스 감지
import { useParams, useNavigate, useLocation } from "react-router-dom";

// MUI Components
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CardContent from "@mui/material/CardContent";
import { styled } from "@mui/material/styles";
import MuiCard from "@mui/material/Card";
import InputAdornment from "@mui/material/InputAdornment";

import CustomTextField from "../../components/CustomTextField";

import LoginWrapper from "./LoginWrapper";

import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

// 유효성 검증 라이브러리
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { useAuth } from "../../hooks/useAuth";

const Card = styled(MuiCard)(({ theme }) => ({
  width: "100%",
  maxWidth: 450, // 최대 너비 제한
  // [theme.breakpoints.up("sm")]: { width: "450px" }, // 기존 코드 제거
}));

const schema = yup.object().shape({
  id: yup
    .string()
    .required("ID를 입력해주세요.")
    .min(3, "ID는 3글자 이상이어야 합니다.")
    .matches(
      /^[가-힣a-zA-Z][^!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?\s]*$/,
      "ID를 확인해주세요."
    ),
  password: yup
    .string()
    .required("비밀번호를 입력해주세요.")
    .min(4, "비밀번호는 4글자 이상이어야 합니다."),
});

const defaultValues = {
  password: "",
  id: "",
};

function LoginPage() {
  const user = useSelector((state) => state.auth.user);
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const { deviceId, errorMessage, setErrorMessage } = useContext(AuthContext);
  const { groupcode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useAuth();

  const [isLoading, setIsLoading] = useState(true);
  const [isPasswordShown, setIsPasswordShown] = useState(false);

  const handleClickShowPassword = () => setIsPasswordShown((show) => !show);

  const {
    control,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues,
    mode: "onBlur",
    resolver: yupResolver(schema),
  });

  const onSubmit = (data) => {
    const { id, password } = data;
    // 로컬 스토리지에서 device_id 가져오기

    auth.login({ id, password, group_code: groupcode, device_id: deviceId }, () => {
      setError("id", {
        type: "manual",
        message: "id 혹은 비밀번호를 확인해주세요.",
      });
      setErrorMessage("로그인 실패: 아이디 혹은 비밀번호를 확인해주세요.");
    });
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      // 세션 스토리지에서 원래 경로 가져오기
      const savedPath = sessionStorage.getItem("redirectPath");
      
      // location.state에서 from 경로 가져오기 (ProtectedRoute에서 설정)
      const fromPath = location.state?.from;
      
      // 리다이렉트 우선순위: 
      // 1. 세션 스토리지에 저장된 경로
      // 2. location.state에서 가져온 경로
      // 3. 기본 경로 (모바일이면 /mobile, 아니면 /projects)
      const redirectPath = savedPath || fromPath || (isMobile ? "/mobile" : "/projects");
      
      // 탭 이름 설정 시도 (브라우저에 따라 제한될 수 있음)
      if (redirectPath === "/projects") {
        try {
          window.name = "projects_tab";
        } catch (error) {
          console.log("탭 이름 설정 실패:", error);
        }
      }
      
      sessionStorage.removeItem("redirectPath");
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, user, navigate, location.state]);

  useEffect(() => {
    const verifyGroupCode = async () => {
      setIsLoading(true);
      try {
        const response = await axiosInstance.get(`/auth/group/${groupcode}`);
        console.log("API 응답 데이터:", response.data);
        if (!response.data?.isValid) {
          navigate("/", { replace: true });
        }
        } catch (error) {
          console.error("그룹 코드 검증 중 오류 발생:", error);
          navigate("/", { replace: true });
        } finally {
          setIsLoading(false);
        }
      };
    verifyGroupCode();
  }, [groupcode, navigate]);
  
  if (isLoading) {
    return <p>로딩 중... 그룹 정보를 확인하고 있습니다.</p>;
  }

  return (
    <LoginWrapper>
      <Card>
        <CardContent
          sx={{ 
            p: (theme) => ({
              xs: `${theme.spacing(6, 4, 6)} !important`, // 모바일: 상6, 좌우4, 하6
              sm: `${theme.spacing(10.5, 8, 8)} !important` // PC: 상10.5, 좌우8, 하8
            })
          }}
        >
          <Box
            sx={{
              mb: { xs: 4, sm: 8 }, // 모바일에서는 마진 축소
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography 
              variant="h2" 
              sx={{ 
                ml: 2.5, 
                fontWeight: 700,
                fontSize: { xs: '1.75rem', sm: '2.5rem' } // 폰트 사이즈 반응형 조정
              }}
            >
              인사이트윈
            </Typography>
          </Box>
          <Box sx={{ mb: { xs: 4, sm: 8 } }}>
            <Typography 
              variant="h3" 
              sx={{ 
                mb: 2.5,
                fontSize: { xs: '1.25rem', sm: '2rem' } // 폰트 사이즈 반응형 조정
              }}
            >
              디지털트윈 플랫폼
            </Typography>
          </Box>

          {errorMessage && (
            <Alert
              severity="error"
              onClose={() => setErrorMessage("")}
              sx={{ mb: 4 }}
            >
              {errorMessage}
            </Alert>
          )}

          <form noValidate autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            <Controller
              name="id"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur } }) => (
                <CustomTextField
                  autoFocus
                  fullWidth
                  label="아이디"
                  sx={{ mb: 6 }}
                  value={value}
                  onBlur={onBlur}
                  onChange={onChange}
                  placeholder="ID를 입력하세요."
                  error={Boolean(errors.id)}
                  {...(errors.id && { helperText: errors.id.message })}
                />
              )}
            />
            <Controller
              name="password"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange, onBlur } }) => (
                <CustomTextField
                  fullWidth
                  value={value}
                  onBlur={onBlur}
                  sx={{
                    mb: 1.5,
                    '& input[type="password"]::-ms-reveal': {
                      display: "none",
                    },
                    '& input[type="password"]::-ms-clear': {
                      display: "none",
                    },
                  }}
                  label="패스워드"
                  onChange={onChange}
                  placeholder="············"
                  id="outlined-adornment-password"
                  error={Boolean(errors.password)}
                  {...(errors.password && {
                    helperText: errors.password.message,
                  })}
                  type={isPasswordShown ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          edge="end"
                          onClick={handleClickShowPassword}
                          onMouseDown={(e) => e.preventDefault()}
                        >
                          {isPasswordShown ? (
                            <VisibilityIcon />
                          ) : (
                            <VisibilityOffIcon />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <Button
              fullWidth
              type="submit"
              variant="contained"
              sx={{ mb: 4, mt: 6, fontSize: { xs: "1.1rem", sm: "1.4rem" } }} // 버튼 텍스트도 반응형으로
            >
              로그인
            </Button>
          </form>
        </CardContent>
      </Card>
    </LoginWrapper>
  );
}

export default LoginPage;
